import setDynamicFormFieldValue from "@iewccouk/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@iewccouk/Store/Reducers";
import { DynamicFormContext } from "@iewccouk/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@iewccouk/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@iewccouk/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    maxLength = "maxLength",
    isMultiline = "isMultiline",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.maxLength]: number | null;
        [fields.isMultiline]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "FirstName";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

const longTextPattern = /comment|description|message|feedback|note/i;

const textAreaCss = css`
    width: 100%;
    min-height: 120px;
    resize: vertical;
    padding-top: 10px;
    padding-bottom: 10px;
    box-sizing: border-box;
`;

/** Salesforce Dynamic Form text field widget. */
const DynamicTextField: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, hintText, isRequired, maxLength, isMultiline } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const resolvedMaxLength = maxLength && maxLength > 0 ? maxLength : undefined;
    const useTextArea = !!isMultiline || longTextPattern.test(`${label} ${fieldKey}`);
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    const validate = React.useCallback(() => {
        const value = fieldValue == null ? "" : String(fieldValue);
        let error = "";

        if (isRequired && !value.trim()) {
            error = siteMessage("Field_Required", label) as string;
        } else if (resolvedMaxLength && value.length > resolvedMaxLength) {
            error = translate("{0} cannot exceed {1} characters", label, String(resolvedMaxLength));
        }

        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label, resolvedMaxLength]);

    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        if (typeof fieldValue !== "undefined") {
            validate();
        }
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, fieldValue, validate, formContext.validators]);

    const sharedProps = {
        label,
        required: isRequired,
        placeholder: hintText,
        value: fieldValue || "",
        maxLength: resolvedMaxLength,
        error: errorMessage,
    };

    return (
        <DynamicFormFieldShell>
            {useTextArea ? (
                <TextArea
                    {...sharedProps}
                    rows={5}
                    mergeCss={true}
                    css={textAreaCss}
                    onChange={event =>
                        setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })
                    }
                />
            ) : (
                <TextField
                    {...sharedProps}
                    onChange={event => setDynamicFormFieldValue({ key: fieldKey, value: event.target.value })}
                />
            )}
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicTextField),
    definition: {
        group: widgetGroup,
        displayName: "Text Field",
        icon: "text",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.isMultiline,
                displayName: "Multiline (textarea)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.maxLength,
                displayName: "Max Length",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: null,
                min: 1,
                placeholder: "No limit",
            },
        ],
    },
};

export default widgetModule;
